import { PrescriptionModel } from '@/api/api.schemas';
import clsx from 'clsx';
import { Banner } from '../banner';
import { PharmacyListCard } from '../encounter/pharmacy/pharmacy-list-card';
import { ExpandableInfo, DataRow } from '../encounter/review/expandable-info';
import { useTranslation } from '@/contexts/i18n-context';
import styles from './prescriptions-section.module.css';
import { formatDate } from '@/util/formats';

interface PrescriptionsSectionProps {
  prescriptions: PrescriptionModel[];
  className?: string;
}

const translationBase = 'visit.treatmentPlan.treatment';

export function PrescriptionsSection({ prescriptions, className }: PrescriptionsSectionProps) {
  const { t } = useTranslation();

  if (prescriptions.length === 0) {
    return null;
  }

  const pharmacyGroups = groupPrescriptionsByPharmacy(prescriptions);
  const hasMultiplePharmacies = pharmacyGroups.length > 1;

  return (
    <ExpandableInfo title={t(`${translationBase}.prescriptionsTitle`)} className={className} isOpen>
      <Banner
        type="warning"
        label={t(`${translationBase}.prescriptionWarning`)}
        contentClassName={styles.bannerContent}
        labelClassName="semibold"
      >
        <p>{t(`${translationBase}.prescriptionSideEffects`)}</p>
      </Banner>

      {hasMultiplePharmacies && <p>{t(`${translationBase}.prescriptionListedByPharmacy`)}</p>}

      {pharmacyGroups.map((group, groupIndex) => (
        <div key={group.pharmacyName} className={styles.pharmacyGroup}>
          <p className={clsx('body-1', 'bold', styles.title)}>
            {hasMultiplePharmacies
              ? t(`${translationBase}.pharmacyOf`, {
                  current: String(groupIndex + 1),
                  total: String(pharmacyGroups.length),
                })
              : t(`${translationBase}.pharmacy`)}
          </p>

          <PharmacyListCard
            withoutSelectButton
            pharmacy={{
              id: groupIndex,
              name: group.pharmacyName,
              fullAddress: group.pharmacyAddress ?? null,
              phone: group.pharmacyPhone ?? null,
              hasMailOrder: group.pharmacyHasMailOrder,
            }}
          />

          <div className={styles.prescriptionsContainer}>
            {group.prescriptions.map((rx, rxIndex) => (
              <div key={`${rx.drugName}-${rxIndex}`} className={styles.prescriptionItem}>
                <DataRow title={t(`${translationBase}.date`)}>{formatDate(rx.dateAdded)}</DataRow>
                <DataRow title={t(`${translationBase}.prescription`)}>{rx.drugName}</DataRow>
                {rx.strength && (
                  <DataRow title={t(`${translationBase}.strength`)}>{rx.strength}</DataRow>
                )}
                {rx.drugForm && (
                  <DataRow title={t(`${translationBase}.form`)}>{rx.drugForm}</DataRow>
                )}
                {rx.quantity && (
                  <DataRow title={t(`${translationBase}.quantity`)}>{rx.quantity}</DataRow>
                )}
                {rx.duration && (
                  <DataRow title={t(`${translationBase}.duration`)}>
                    {t(`${translationBase}.durationDays`, { value: rx.duration })}
                  </DataRow>
                )}
                {rx.refills && (
                  <DataRow title={t(`${translationBase}.refills`)}>{rx.refills}</DataRow>
                )}
                {rx.sig && <DataRow title={t(`${translationBase}.directions`)}>{rx.sig}</DataRow>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </ExpandableInfo>
  );
}

interface PharmacyGroup {
  pharmacyName: string;
  pharmacyAddress?: string | null;
  pharmacyPhone?: string | null;
  pharmacyHasMailOrder?: boolean | null;
  prescriptions: PrescriptionModel[];
}

function groupPrescriptionsByPharmacy(prescriptions: PrescriptionModel[]): PharmacyGroup[] {
  const groupMap = new Map<string, PharmacyGroup>();

  for (const rx of prescriptions) {
    const key = rx.pharmacyName ?? 'unknown';
    const existing = groupMap.get(key);

    if (existing) {
      existing.prescriptions.push(rx);
    } else {
      groupMap.set(key, {
        pharmacyName: rx.pharmacyName ?? '',
        pharmacyAddress: rx.pharmacyAddress,
        pharmacyPhone: rx.pharmacyPhone,
        pharmacyHasMailOrder: rx.pharmacyHasMailOrder,
        prescriptions: [rx],
      });
    }
  }

  return Array.from(groupMap.values());
}
